import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  FormControlLabel,
  Checkbox,
  FormGroup,
  Backdrop,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";

import {
  Modal,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Typography,
  InputBase,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Tooltip,
  Switch,
  Popper,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { filterTemplate as _staticFilterTemplate, tableTemplate as _staticTableTemplate, editableColumns as configEditableColumns, auditColumns } from "../config/demandSupplyConfig";
import api from "../api";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  PersonSearch as PersonSearchIcon,
  HowToReg as HowToRegIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";
import ExportIcon from "../components/ExportIcon"; // Adjust the path based on your project structure
import FilterPanel from "./FilterPanel";
import DemandDashboard from "./DemandDashboard";
import { MAINTENANCE_START_HOUR_IST, MAINTENANCE_START_MINUTE_IST, MAINTENANCE_END_HOUR_IST, MAINTENANCE_END_MINUTE_IST } from "../config/maintenanceConfig";

const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";

// --- Maintenance Window Config (IST 24h format) ---
// Edit and Recommendation icons will be disabled during this window.
// Maintenance times are provided from `src/config/maintenanceConfig.js`

/** Format 24h hour+minute into a readable "h:mm AM/PM" string */
const formatISTTime = (hour, minute) => {
  const suffix = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  const m = String(minute).padStart(2, "0");
  return `${h}:${m} ${suffix}`;
};

const MAINTENANCE_START_LABEL = formatISTTime(MAINTENANCE_START_HOUR_IST, MAINTENANCE_START_MINUTE_IST);
const MAINTENANCE_END_LABEL = formatISTTime(MAINTENANCE_END_HOUR_IST, MAINTENANCE_END_MINUTE_IST);
const MAINTENANCE_TOOLTIP = `Under maintenance. Please try after ${MAINTENANCE_END_LABEL} IST.`;
const MAINTENANCE_BANNER_MSG = `System is under maintenance from ${MAINTENANCE_START_LABEL} to ${MAINTENANCE_END_LABEL} IST. Edit and Resource Lookup are temporarily disabled.`;

/**
 * Returns true if the current time (converted to IST) falls within the
 * configured maintenance window.
 */
const isMaintenanceWindow = () => {
  const now = new Date();
  // IST is UTC+5:30
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const istMs = utcMs + 5.5 * 3600000;
  const ist = new Date(istMs);
  const istMinutes = ist.getHours() * 60 + ist.getMinutes();
  const startMinutes = MAINTENANCE_START_HOUR_IST * 60 + MAINTENANCE_START_MINUTE_IST;
  const endMinutes = MAINTENANCE_END_HOUR_IST * 60 + MAINTENANCE_END_MINUTE_IST;
  return istMinutes >= startMinutes && istMinutes < endMinutes;
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
        whiteSpace: "normal", // Allow wrapping
        wordBreak: "break-word", // Break long words if needed
      },
    },
    backgroundColor: primaryColor,
    color: "#FFFFFF !important",
    fontSize: "0.95rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
  },
"& .MuiDataGrid-root":
{ 
  height: "150px",
},
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "normal", // Allow wrapping
  },
  "& .editable-cell": {
    backgroundColor: "#FFFDE7 !important", // Light yellow background for editable cells in view mode
    "&:hover": {
      backgroundColor: "#FFF9C4 !important",
    },
  },
  "& .MuiDataGrid-row.row-editing .MuiDataGrid-cell--editable": {
    backgroundColor: "#FFF59D !important", // Dark yellow background for editable cells in edit mode
    border: "1px solid #005EB8",
  },
  "& .MuiDataGrid-row.row-editing .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable)":
  {
    backgroundColor: "#E0E0E0 !important",
  },
  "& .assigned-row": {
    backgroundColor: "#E8F5E9",
    "& .MuiDataGrid-cell": {
      color: "#1B5E20",
    },
  },
  "& .fp-proposed": {
    backgroundColor: "#FFF9C4",
    "& .MuiDataGrid-cell": {
      color: "#795548",
    },
  },
}));

// Checkbox icons used by Autocomplete multi-select renderOption
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;



const DropdownEditCell = React.memo(
  ({ field, value, id, api: gridApi, options, row }) => {
    const [localOptions, setLocalOptions] = useState(options);
    const [selectedValue, setSelectedValue] = useState(value);


    useEffect(() => {
      setSelectedValue(value);
    }, [value]);
    useEffect(() => {
      const fetchDependentDropdown = async () => {

        // Fulfilment Plan depends on Demand Type
        if (field === "Fulfilment Plan" && row["Demand Type"]) {
          try {
            const response = await api.get("/demand/dropdownBySubType", {
              params: { fieldName: "FULFILMENT_PLAN", subType: row["Demand Type"] },
            });
            if (Array.isArray(response.data)) {
              const opts = response.data.map(item => ({
                value: item.key_value,
                label: item.description,
              }));
              setLocalOptions(opts);
              // Always set the first value if the current value is not in the new options
              if (opts.length > 0 && (!value || !opts.some(opt => opt.value === value))) {
                setSelectedValue(opts[0].value);
                gridApi.setEditCellValue({ id, field: "Fulfilment Plan", value: opts[0].value });
              }
            } else {
              setLocalOptions([]);
            }
          } catch {
            setLocalOptions([]);
          }
        }



        // Included In Forecast depends on Demand Type
        if (field === "Included In Forecast" && row["Demand Type"]) {
          try {
            const response = await api.get("/demand/dropdownBySubType", {
              params: { fieldName: "INCLUDED_IN_FORECAST", subType: row["Demand Type"] },
            });
            if (Array.isArray(response.data)) {
              const opts = response.data.map(item => ({
                value: item.key_value,
                label: item.description,
              }));
              setLocalOptions(opts);
              // Set the first value if not already set
              if (opts.length > 0 && (!value || value === "")) {
                setSelectedValue(opts[0].value);
                gridApi.setEditCellValue({ id, field: "Included In Forecast", value: opts[0].value });
              }
            } else {
              setLocalOptions([]);
            }
          } catch {
            setLocalOptions([]);
          }
        }
        // Demand Category depends on Project Billability Type
        if (field === "Demand Category" && row["Project Billability Type"]) {
          try {
            const response = await api.get("/demand/dropdownBySubType", {
              params: { fieldName: "DEMAND_CATEGORY", subType: row["Project Billability Type"] },
            });
            if (Array.isArray(response.data)) {
              const opts = response.data.map(item => ({
                value: item.key_value,
                label: item.description,
              }));
              setLocalOptions(opts);
              // Set the first value if not already set
              if (opts.length > 0 && (!value || value === "")) {
                setSelectedValue(opts[0].value);
                gridApi.setEditCellValue({ id, field: "Demand Category", value: opts[0].value });
              }
            } else {
              setLocalOptions([]);
            }
          } catch {
            setLocalOptions([]);
          }
        }
        // Demand Status depends on Demand Type
        if (field === "Demand Status" && row["Demand Type"]) {
          try {
            const response = await api.get("/demand/dropdownBySubType", {
              params: { fieldName: "DEMAND_STATUS", subType: row["Demand Type"] },
            });
            if (Array.isArray(response.data)) {
              const opts = response.data.map(item => ({
                value: item.key_value,
                label: item.description,
              }));
              setLocalOptions(opts);
              if (opts.length > 0 && (!value || value === "")) {
                setSelectedValue(opts[0].value);
                gridApi.setEditCellValue({ id, field: "Demand Status", value: opts[0].value });
              }
            } else {
              setLocalOptions([]);
            }
          } catch {
            setLocalOptions([]);
          }
        }
        // Supply Source depends on Fulfilment Plan
        if (field === "Supply Source" && row["Fulfilment Plan"]) {
          try {
            const response = await api.get("/demand/dropdownBySubType", {
              params: { fieldName: "SUPPLY_SOURCE", subType: row["Fulfilment Plan"] },
            });
            if (Array.isArray(response.data)) {
              const opts = response.data.map(item => ({
                value: item.key_value,
                label: item.description,
              }));
              setLocalOptions(opts);
              if (opts.length > 0 && (!value || value === "")) {
                setSelectedValue(opts[0].value);
                gridApi.setEditCellValue({ id, field: "Supply Source", value: opts[0].value });
              }
            } else {
              setLocalOptions([]);
            }
          } catch {
            setLocalOptions([]);
          }
        }

        if (field === "Demand Category" && row["Demand Type"]) {
          try {

            const subType = row["Demand Type"] === "Conversion" || row["Demand Type"] === "LMT -Visa Purpose" ? row["Demand Type"] : row["Project Billability Type"];
            const response = await api.get("/demand/dropdownBySubType", {
              params: { fieldName: "DEMAND_CATEGORY", subType: subType },
            });
            if (Array.isArray(response.data)) {
              const opts = response.data.map(item => ({
                value: item.key_value,
                label: item.description,
              }));
              setLocalOptions(opts);
              // Set the first value if not already set
              if (opts.length > 0 && (!value || value === "")) {
                setSelectedValue(opts[0].value);
                gridApi.setEditCellValue({ id, field: "Demand Category", value: opts[0].value });
              }
            } else {
              setLocalOptions([]);
            }
          } catch {
            setLocalOptions([]);
          }
        }
        // Default for other fields
        else if (
          field !== "Demand Status" &&
          field !== "Supply Source" &&
          field !== "Demand Category" &&
          field !== "Included In Forecast" &&
          field !== "Fulfilment Plan"
        ) {
          setLocalOptions(options);
        }
      };
      fetchDependentDropdown();
      // eslint-disable-next-line
    }, [field, row["Project Billability Type"], row["Demand Type"], row["Fulfilment Plan"], options]);

    const handleChange = async (event) => {
      const newValue = event.target.value;
      setSelectedValue(newValue);

      if (field === "Demand Type") {
        gridApi.setEditCellValue({ id, field, value: newValue });
        gridApi.setEditCellValue({ id, field: "Demand Status", value: "" });
        gridApi.setEditCellValue({ id, field: "Included In Forecast", value: "" });
        gridApi.setEditCellValue({ id, field: "Demand Category", value: "" });
        gridApi.setEditCellValue({ id, field: "Fulfilment Plan", value: "" }); // This triggers Fulfilment Plan cell to re-render
      } else if (field === "Fulfilment Plan") {
        gridApi.setEditCellValue({ id, field, value: newValue });
        gridApi.setEditCellValue({ id, field: "Supply Source", value: "" });
      } else if (field === "Project Billability Type") {
        gridApi.setEditCellValue({ id, field, value: newValue });
        gridApi.setEditCellValue({ id, field: "Demand Category", value: "" });
      } else {
        gridApi.setEditCellValue({ id, field, value: newValue });
      }
    };

    return (
      <Select
        value={selectedValue || ""}
        onChange={handleChange}
        sx={{ width: "100%" }}
      >
        {localOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  }
);


function getWeekOfMonth(date) {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 for Sunday, 1 for Monday, etc.
  const dayOfMonth = date.getDate();
  let weekNumber = Math.ceil((dayOfMonth + firstDayOfWeek) / 7);
  return weekNumber;
}

const DateFieldEditCell = React.memo(({ field, value, id, api: gridApi }) => {
  const [date, setDate] = useState(value ? new Date(value) : null);

  const handleChange = (newDate) => {
    setDate(newDate);
    if (newDate) {
      // Only update AllocationWeek if the field is JoiningAllocationDate
      if (field === "Allocation Date") {
        const weekNumber = getWeekOfMonth(newDate);
        const monthName = newDate.toLocaleString("default", { month: "short" });
        const allocationWeek = `${monthName}-Week ${weekNumber}`;
        gridApi.setEditCellValue({
          id,
          field: "Allocation Week",
          value: allocationWeek,
        });

        // Set Eff Month (format: YYYY-MM)
        const monthNameEff = newDate.toLocaleString("default", { month: "short" });
        const effMonth = `${monthNameEff}-${newDate.getFullYear()}`;
        gridApi.setEditCellValue({
          id,
          field: "Eff Month",
          value: effMonth,
        });
      }
    } else if (field === "Allocation Date") {
      // Only clear AllocationWeek if JoiningAllocationDate is cleared
      gridApi.setEditCellValue({
        id,
        field: "Allocation Week",
        value: "",
      });
      gridApi.setEditCellValue({
        id,
        field: "Eff Month",
        value: "",
      });
    }

    // Update the actual date field
    gridApi.setEditCellValue({
      id,
      field,
      value: newDate ? newDate.toISOString().split("T")[0] : null,
    });
  };

  return (
    <TextField
      type="date"
      value={date ? date.toISOString().split("T")[0] : ""}
      onChange={(e) => {
        const newDate = e.target.value ? new Date(e.target.value) : null;
        handleChange(newDate);
      }}
      sx={{ width: "100%" }}
    />
  );
});

const EmployeeIdEditCell = React.memo(({ field, value, id, api: gridApi }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleBlur = async () => {
    // Always update the ID field, even if empty
    gridApi.setEditCellValue({
      id,
      field,
      value: inputValue,
    });

    if (inputValue) {
      setIsLoading(true);
      try {
        // Fetch employee name, grade, hrStatus, and constraint
        const response = await api.get("/employees/getEmployeeById", {
          params: { employeeId: inputValue },
        });
        console.log("Employee responses:", response.data);
        const data = response.data || {};
        // If backend returned a message or error without employee details, surface it
        if ((data.message || data.error) && !data.employee_name && !data.employee_id) {
          const msg = data.message || data.error || "Employee is not available in the selected supply source.";
          // Dispatch global event so the component can show the toast
          try { window.dispatchEvent(new CustomEvent('employeeLookupError', { detail: msg })); } catch (e) {}
          // Clear fields in the grid
          gridApi.setEditCellValue({ id, field: "Employee Name", value: "" });
          gridApi.setEditCellValue({ id, field: "Grades", value: "" });
          gridApi.setEditCellValue({ id, field: "HR Status", value: "" });
          gridApi.setEditCellValue({ id, field: "Constraint", value: "" });
          gridApi.setEditCellValue({ id, field: "SEF", value: "" });
          gridApi.setEditCellValue({ id, field: "Hacker Rank Score", value: "" });
        } else if (data && (data.employee_name || data.employee_id || data.employeeId === 0)) {
          gridApi.setEditCellValue({ id, field: "Employee Name", value: data.employee_name || data.employeeName || "" });
          gridApi.setEditCellValue({ id, field: "Grades", value: data.grade || "" });
          gridApi.setEditCellValue({ id, field: "HR Status", value: data.hrStatus || data.hr_status || "" });
          gridApi.setEditCellValue({ id, field: "Constraint", value: data.constraint || "" });
          gridApi.setEditCellValue({ id, field: "SEF", value: data.sef || "" });
          gridApi.setEditCellValue({ id, field: "Hacker Rank Score", value: data.hacker_rank_score || data.hackerRankScore || "" });
        } else {
          // Fallback: clear fields
          gridApi.setEditCellValue({ id, field: "Employee Name", value: "" });
          gridApi.setEditCellValue({ id, field: "Grades", value: "" });
          gridApi.setEditCellValue({ id, field: "HR Status", value: "" });
          gridApi.setEditCellValue({ id, field: "Constraint", value: "" });
          gridApi.setEditCellValue({ id, field: "SEF", value: "" });
          gridApi.setEditCellValue({ id, field: "Hacker Rank Score", value: "" });
        }
      } catch (error) {
        console.error("Error fetching employee name:", error);
        // Try to extract backend message from axios error
        const msg = (error && error.response && (error.response.data?.message || error.response.data?.error)) || error.message || "Unable to lookup employee for the selected source. Please try again.";
        try { window.dispatchEvent(new CustomEvent('employeeLookupError', { detail: msg })); } catch (e) {}
        gridApi.setEditCellValue({ id, field: "Employee Name", value: "" });
        gridApi.setEditCellValue({ id, field: "Grades", value: "" });
        gridApi.setEditCellValue({ id, field: "HR Status", value: "" });
        gridApi.setEditCellValue({ id, field: "Constraint", value: "" });
        gridApi.setEditCellValue({ id, field: "SEF", value: "" });
        gridApi.setEditCellValue({ id, field: "Hacker Rank Score", value: "" });
      } finally {
        setIsLoading(false);
      }
    } else {
      // If input is empty, clear related fields
      gridApi.setEditCellValue({
        id,
        field: "Employee Name",
        value: "",
      });
      gridApi.setEditCellValue({
        id,
        field: "Grades",
        value: "",
      });
      gridApi.setEditCellValue({
        id,
        field: "HR Status",
        value: "",
      });
      gridApi.setEditCellValue({
        id,
        field: "Constraint",
        value: "",
      });
    }
  };

  return (
    <TextField
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Enter Employee ID"
      sx={{ width: "100%" }}
      disabled={isLoading}
      InputProps={{
        endAdornment: isLoading ? <CircularProgress size={20} /> : null,
      }}
    />
  );
});

function DemandSupplyMatchingV1() {
  const REPORT_KEY = "demand_supply_matching_column_visibility";
  const DEMAND_CONFIG_KEY = "demand_report_column_visibility";
  const baseURI = process.env.REACT_APP_API_BASE_URL ?? "";
 
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [initialData,setInitialData]=useState([]);

  // Live config from DB (falls back to static config)
  const [tableTemplate, setTableTemplate] = useState(_staticTableTemplate);
  const [filterTemplate, setFilterTemplate] = useState(_staticFilterTemplate);
  const [configReady, setConfigReady] = useState(false);
  const template = filterTemplate;

  // Maintenance window state ΓÇô re-evaluated every 30 seconds
  const [underMaintenance, setUnderMaintenance] = useState(isMaintenanceWindow);
  useEffect(() => {
    const interval = setInterval(() => setUnderMaintenance(isMaintenanceWindow()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch live config from API on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${baseURI}/api/report-config/${DEMAND_CONFIG_KEY}`);
        if (res.ok) {
          const data = await res.json();
          console.log("[CONFIG] Loaded report-config from API, table_template length:", data.table_template?.length);
          if (Array.isArray(data.table_template) && data.table_template.length > 0)
            setTableTemplate(data.table_template);
          if (Array.isArray(data.filter_template) && data.filter_template.length > 0)
            setFilterTemplate(data.filter_template);
        } else {
          console.warn("[CONFIG] report-config API returned status:", res.status, "- using static fallback");
        }
      } catch (err) {
        console.warn("[CONFIG] report-config fetch failed, using static fallback:", err.message);
      } finally {
        setConfigReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [columns, setColumns] = useState([]);
  const [searchText, setSearchText] = useState("");
  const gridRef = useRef(null);
  const clearAllRef = useRef(null);
  const saveFilterRef = useRef(null);
  const activeFiltersRef = useRef(null);
  const [saveFilterMsg, setSaveFilterMsg] = useState("");
  const [showFiltersOpen, setShowFiltersOpen] = useState(false);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem(REPORT_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(REPORT_KEY, JSON.stringify(newModel));
  };

  const [parentCustomer, setParentCustomer] = useState("All");
  const [buDesc, setBuDesc] = useState("All");
  const [pdlName, setPdlName] = useState("All");
  const [offOn, setOffOn] = useState("All");

  const [parentCustomerOptions, setParentCustomerOptions] = useState([]);
  const [buDescOptions, setBuDescOptions] = useState([]);
  const [pdlNameOptions, setPdlNameOptions] = useState([]);
  const [offOnOptions, setOffOnOptions] = useState([]);

  const [auditOpen, setAuditOpen] = useState(false);
  const [selectedUnique, setSelectedUnique] = useState(null);
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState({});

  const [validationOpen, setValidationOpen] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const lastEditRowId = useRef(null);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const [releaseOverrides, setReleaseOverrides] = useState(null);
  const [confirmSupplyChangeOpen, setConfirmSupplyChangeOpen] = useState(false);
  const [pendingSupplyValue, setPendingSupplyValue] = useState(null);
  const [confirmCancelledOpen, setConfirmCancelledOpen] = useState(false);
  const [pendingDemandStatusValue, setPendingDemandStatusValue] = useState(null);

  const editableConfig = configEditableColumns;
  const editableMeta = Array.isArray(editableConfig)
    ? editableConfig.map((item) => {
        if (typeof item === 'string') {
          return { apiField: item, field: item, headerName: item };
        }
        // item is object
        const apiField = item.apiField || item.field;
        const headerName = item.headerName || item.field || apiField;
        const fieldDisplay = item.field && item.apiField ? item.field : headerName;
        return { apiField, field: fieldDisplay, headerName };
      })
    : [];

  // editableColumns used throughout code should be api field names (data keys)
  const editableColumns = editableMeta.map((e) => e.apiField);
  const editableHeaderMap = editableMeta.reduce((m, c) => { m[c.apiField] = c.headerName || c.apiField; return m; }, {});


  const fieldToDropdownTypeMap = {
    "Demand Category": "DEMAND_CATEGORY",
    "Fulfilment Plan": "FULFILMENT_PLAN",
    "Supply Source": "SUPPLY_SOURCE",
    "Demand Type": "DEMAND_TYPE",
    "Demand Status": "DEMAND_STATUS",
    Grades: "GRADE",
    "Included In Forecast": "YES_NO",
    "Cross Skill Required": "YES_NO",
  };

  // Add Action dropdown options directly
  const actionDropdownOptions = [
    { value: "Allocated", label: "Allocated" },
    { value: "AWIP", label: "AWIP" },
    { value: "Proposed", label: "Proposed" },
    { value: "Available", label: "Available" },
       { value: "Rejected", label: "Rejected" },
    { value: "Release From SO", label: "Release From SO" },
    { value: "Reskill", label: "Reskill" },
    { value: "Resigned/ML/Others", label: "Resigned/ML/Others" },
    { value: "Need to Exit", label: "Need to Exit" },
  ];

  useEffect(() => {
    let isMounted = true;
    const fetchAllDropdownOptions = async () => {
      const options = {};
      for (const field in fieldToDropdownTypeMap) {
        const dropdownType = fieldToDropdownTypeMap[field];
        try {
          const response = await api.get(`/demand/dropdown`, {
            params: { fieldName: dropdownType },
          });
          options[field] = response.data.map((item) => ({
            value: item.key_value,
            label: item.description,
          }));
        } catch (error) {
          console.error(`Error fetching dropdown options for ${field}:`, error);
          options[field] = []; // Ensure there's always a value
        }
      }
      // --- Inject "NA" option into Included In Forecast if not already present ---
      if (options["Included In Forecast"]) {
        const hasNA = options["Included In Forecast"].some(
          (o) => String(o.value).toUpperCase() === "NA" || String(o.label).toUpperCase() === "NA"
        );
        if (!hasNA) {
          options["Included In Forecast"].push({ value: "NA", label: "NA" });
        }
      }

      if (isMounted) {
        setDropdownOptions(options);
      }
    };

    const fetchDropdownData = async () => {
      try {
        const [
          parentCustomerResponse,
          buDescResponse,
          pdlNameResponse,
          offOnResponse,
        ] = await Promise.all([
          api.get("/demandselect/parentCustomers"),
          api.get("/demandselect/businessUnitDescs"),
          api.get("/demandselect/pdlNames"),
          api.get("/demandselect/offOns"),
        ]);

        if (isMounted) {
          setParentCustomerOptions(["All", ...parentCustomerResponse.data]);
          setBuDescOptions(["All", ...buDescResponse.data]);
          setPdlNameOptions(["All", ...pdlNameResponse.data]);
          setOffOnOptions(["All", ...offOnResponse.data]);
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchAllDropdownOptions();
    fetchDropdownData();
    return () => {
      isMounted = false;
    };
  }, []);


function reorderByTemplate(data = [], template = [], options = {}) {
  const { moveToEnd = [] } = options;

  // Normalize template to array of trimmed strings
  const templateKeys = Array.isArray(template)
    ? template.map(String)
    : String(template).split(",").map((s) => s.trim()).filter(Boolean);

  return data.map((obj) => {
    const newObj = {};
    const added = new Set();

    // 1) Add keys from template in order if they exist on the object
    for (const key of templateKeys) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = obj[key];
        added.add(key);
      }
    }

    // 2) Add remaining keys in original object order, skipping keys that will be moved to end
    for (const key of Object.keys(obj)) {
      if (added.has(key)) continue;
      if (moveToEnd.includes(key)) continue;
      newObj[key] = obj[key];
      added.add(key);
    }

    // 3) Append moveToEnd keys in the order specified (if present)
    for (const key of moveToEnd) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = obj[key];
      }
    }

    return newObj;
  });
}

  // Re-apply template ordering + rebuild column headers whenever data or config changes
  // Waits for configReady so that DB headerNames are applied (not just static fallback)
  useEffect(() => {
    if (!configReady || !rawData.length || !tableTemplate.length) return;

    // Build field ΓåÆ headerName map from config (DB or static fallback)
    // Config headerNames take full priority; specialHeaderNameMap is only used as
    // a safety-net for fields missing from the config.
    const configHeaderMap = tableTemplate.reduce((m, c) => {
      if (typeof c === 'object' && c !== null) {
        m[c.field] = c.headerName || c.field;
      }
      return m;
    }, {});

    // Safety-net: ensure SO-prefixed fields always have a sane header even if
    // missing from config.  Because configHeaderMap is spread LAST it will
    // override the fallback whenever a config entry exists.
    const fallbackHeaderMap = {
      "Rotation SO": "Rotation SO",
      "SO Id": "SO Id",
      "SO Line Status": "SO Line Status",
      "SO Grade": "SO Grade",
      "SO Submission Date": "SO Submission Date",
      "SO Billability": "SO Billability",
      "Open SO Ageing": "Open SO Ageing",
      "Open SO Ageing Range": "Open SO Ageing Range",
    };

    // configHeaderMap is spread LAST ΓåÆ DB / static headerNames always win
    const headerNameMap = { ...fallbackHeaderMap, ...configHeaderMap };

    console.log("[COLUMNS] Rebuilding columns. configReady:", configReady,
      "templateSample:", JSON.stringify((tableTemplate.slice(0,3) || []).map(c => ({ f: c.field, h: c.headerName }))));

    const templateFields = Array.isArray(tableTemplate) && typeof tableTemplate[0] === "object"
      ? tableTemplate.map((t) => t.field)
      : tableTemplate;

    // Reorder data keys according to updated template
    const reordered = reorderByTemplate(rawData, templateFields, { moveToEnd: ["item_id"] });
    setData(reordered);
    setInitialData(reordered);

    // Build final column definitions
    const cols = Object.keys(reordered[0])
      .filter((key) => key !== "Recommendations")
      .map((key) => ({
        field: key,
        headerName: headerNameMap[key] || key,
        minWidth: 120,
        flex: 1,
      }));
    setColumns(cols);

    // Apply hidden flags from config
    const hiddenMap = {};
    tableTemplate.forEach((c) => { if (c.hidden) hiddenMap[c.field] = false; });
    if (Object.keys(hiddenMap).length > 0) {
      setColumnVisibilityModel((prev) => ({ ...hiddenMap, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configReady, rawData, tableTemplate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Mark 1");
        if (!chartView) setLoading(true);
        const response = await api.get("/demandselect");

           const templateFields = Array.isArray(tableTemplate) && tableTemplate.length && typeof tableTemplate[0] === 'object'
             ? tableTemplate.map((t) => t.field)
             : tableTemplate;

        let fetchedData = reorderByTemplate(response.data, templateFields, { moveToEnd: ["item_id"] });
      

        fetchedData = fetchedData.map((item) => {
          if (item.JoiningAllocationDate) {
            const joiningDate = new Date(item.JoiningAllocationDate);
            const weekNumber = getWeekOfMonth(joiningDate);
            const monthName = joiningDate.toLocaleString("default", {
              month: "short",
            });
            item.AllocationWeek = `${monthName}-Week ${weekNumber}`;
            item.JoiningAllocationDate =
              item.JoiningAllocationDate.split("T")[0];
          }
          return item;
        });
        // Filter out empty rows
        fetchedData = fetchedData.filter((row) => {
          const keys = Object.keys(row).filter(k => k !== 'item_id' && k !== 'id');
          return keys.some(k => row[k] !== null && row[k] !== undefined && row[k] !== '');
        });
        setRawData(fetchedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Add this inside DemandSupplyMatching(), near other handlers
  // const handleRecommendationsClick = useCallback((row) => {
  //   // TODO: Replace with real behavior (open a dialog, call an API, etc.)
  //   console.log("Recommendations clicked for SO:", row["SO Id"], row);
  //   // alert(`"Recommendations clicked for SO:", $row["So Id"], $row`)


  //   setOpen(true)
  // }, []);

  

  const handleEditClick = (id) => () => {
    const row = data.find((r) => r.item_id === id);
    if (row) {
      setEditingRow(row);
      // Initialize form data with current row values for editable columns
      const formData = {};
      editableColumns.forEach((col) => {
        formData[col] = row[col] || "";
      });
      // Proposed Date is read-only; it auto-populates from table data (no default to today)
      const todayStr = new Date().toISOString().split("T")[0];
      // Default Allocation Date to today if empty
      if ((!formData["Allocation Date"] || String(formData["Allocation Date"]).trim() === "") && (!formData["Demand Status"] || String(formData["Demand Status"]).toLowerCase() !== "to be cancelled")) {
        formData["Allocation Date"] = todayStr;
      }

      // Default Action Date to today if empty
      if (!formData["Action Date"] || String(formData["Action Date"]).trim() === "") {
        formData["Action Date"] = todayStr;
      }

      // Normalize Included In Forecast values (e.g., 'Y'/'Yes' -> option value)
      try {
        if (formData["Included In Forecast"] && dropdownOptions["Included In Forecast"]) {
          formData["Included In Forecast"] = mapYesNoToOption(formData["Included In Forecast"]);
        }
      } catch (e) {}

      // Default SO Reserve Status from persisted default if empty
      try {
        const defaultReserve = localStorage.getItem("defaultSoReserveStatus");
        if ((!formData["SO Reserve Status"] || String(formData["SO Reserve Status"]).trim() === "") && defaultReserve) {
          formData["SO Reserve Status"] = defaultReserve;
        }
      } catch (e) {}
      setEditFormData(formData);
      setEditModalOpen(true);
    }
  };

  // Helper to map various yes/no representations to the dropdown option value
  const mapYesNoToOption = (raw) => {
    const rv = raw === undefined || raw === null ? "" : String(raw).trim();
    if (!rv) return "";
    const normalized = rv.toLowerCase();
    // Handle "NA" explicitly
    if (normalized === "na") {
      const opts = dropdownOptions["Included In Forecast"] || [];
      const naMatch = opts.find((o) => String(o.value).toUpperCase() === "NA" || String(o.label).toUpperCase() === "NA");
      if (naMatch) return naMatch.value;
      return "NA";
    }
    const yesVals = new Set(["y", "yes", "true", "1"]);
    const noVals = new Set(["n", "no", "false", "0"]);
    const opts = dropdownOptions["Included In Forecast"] || [];

    let desired = null;
    if (yesVals.has(normalized)) desired = "yes";
    else if (noVals.has(normalized)) desired = "no";

    if (desired) {
      const match = opts.find(
        (o) => (o.label || "").toString().toLowerCase().includes(desired) || String(o.value || "").toLowerCase().includes(desired)
      );
      if (match) return match.value;
    }

    // fallback: try to match exact value or label
    const exactVal = opts.find((o) => String(o.value || "").toLowerCase() === normalized);
    if (exactVal) return exactVal.value;
    const labelMatch = opts.find((o) => (o.label || "").toString().toLowerCase() === normalized || (o.label || "").toString().toLowerCase().includes(normalized));
    if (labelMatch) return labelMatch.value;

    return raw;
  };

  // Handle form field change in edit modal
  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => {
      const newData = { ...prev, [field]: value };
      
      // Clear dependent fields when parent changes
      if (field === "Project Billability Type") {
        newData["Demand Category"] = "";
      }
      // When Action changes, auto-set Action Date to today
      if (field === "Action") {
        newData["Action Date"] = new Date().toISOString().split("T")[0];
      }
      
      return newData;
    });
  };

  // Special handler for Demand Status changes: if changing to 'to be cancelled' and employee is filled,
  // show a confirmation popup that Action will be set to Release and employee freed for other SOs.
  const handleDemandStatusChange = (newValue) => {
    const newLower = String(newValue || "").trim().toLowerCase();
    if (newLower === "to be cancelled") {
      const empId = editFormData["Employee Id"] || "";
      const empName = editFormData["Employee Name"] || "";
      const hasEmployee = (empId && String(empId).trim()) || (empName && String(empName).trim());
      if (hasEmployee) {
        setPendingDemandStatusValue(newValue);
        setConfirmCancelledOpen(true);
        return;
      }
    }
    handleEditFormChange("Demand Status", newValue);
  };

  const confirmCancelledChange = () => {
    setEditFormData((prev) => ({
      ...prev,
      "Demand Status": pendingDemandStatusValue,
      "Action": "Release From SO",
      "Action Date": new Date().toISOString().split("T")[0],
      "Employee Id": "",
      "Employee Name": "",
      "Grades": "",
    }));
    setPendingDemandStatusValue(null);
    setConfirmCancelledOpen(false);
  };

  const cancelCancelledChange = () => {
    setPendingDemandStatusValue(null);
    setConfirmCancelledOpen(false);
  };

  // Special handler for Supply Source changes: if an employee is already selected/entered,
  // ask for confirmation because changing supply source will clear employee details.
  const handleSupplySourceSelectChange = (newValue) => {
    const empId = editFormData["Employee Id"] || editFormData["EmployeeId"] || "";
    const empName = editFormData["Employee Name"] || "";
    const empGrade = editFormData["Grades"] || "";
    const hasEmployee = (empId && String(empId).trim()) || (empName && String(empName).trim()) || (empGrade && String(empGrade).trim());
    if (hasEmployee) {
      setPendingSupplyValue(newValue);
      setConfirmSupplyChangeOpen(true);
    } else {
      handleEditFormChange("Supply Source", newValue);
    }
  };

  const confirmSupplyChange = () => {
    // Clear employee-related fields but apply the pending supply source value to the form.
    const clearOverrides = {
      "Employee Id": "",
      "Employee Name": "",
      "Grades": "",
      "HR Status": "",
      "Constraint": "",
    };
    setEditFormData((prev) => ({ ...prev, ...clearOverrides, "Supply Source": pendingSupplyValue }));
    setPendingSupplyValue(null);
    setConfirmSupplyChangeOpen(false);
  };

  const cancelSupplyChange = () => {
    setPendingSupplyValue(null);
    setConfirmSupplyChangeOpen(false);
  };

  // Determine if a given field is required in the edit modal based on current form state
  const isFieldRequired = (apiField) => {
    // Always required fields per UX request
    // Note: Action is NOT mandatory; Action Date IS mandatory; Remarks Details is optional.
    const alwaysRequired = new Set([
      "Demand Status",
      "Included In Forecast",
      "Fulfilment Plan",
      "Supply Source",
      "Employee Id",
      "Employee Name",
      "Grades",
      "Action Date",
    ]);
    if (alwaysRequired.has(apiField)) return true;
    const fp = (editFormData["Fulfilment Plan"] || "").toLowerCase();
    const offOnVal = (editFormData["Off Onsite"] || "").toLowerCase();
    const demandStatus = (editFormData["Demand Status"] || "").toLowerCase();

    const fpNeedsEmployee = [
      "identified genc",
      "identified fte",
      "cwr conversion",
      "identified hiring in progress",
      "inter project movements"
    ].includes(fp);

    if (apiField === "Employee Id" || apiField === "Employee Name") {
      if (!fpNeedsEmployee) return false;
      // Special case: when identified hiring in progress + onsite, Employee Name required
      if (fp === "identified hiring in progress" && apiField === "Employee Name") {
        return offOnVal === "onsite" || fpNeedsEmployee;
      }
      return true;
    }

    if (apiField === "Rotation SO") {
      return false; // Rotation SO is always optional
    }

    if (apiField === "Allocation Date") {
      // Allocation Date is required unless Demand Status is 'to be cancelled' (handled in render/validation)
      return true;
    }

    return false;
  };

  // Supply sources that support API-based employee lookup.
  // For any other source (e.g. Lateral Hire, Inter Department) the user enters details manually.
  const API_LOOKUP_SOURCES = new Set([
    "pdp","c-deploy", "project nbl","rotation wo backfill","rotation w backfill"
  ]);

  /** Returns true when the current Supply Source supports auto-lookup of employee details via API */
  const isApiLookupSource = () => {
    const src = (editFormData["Supply Source"] || editingRow?.["Supply Source"] || "").toString().trim().toLowerCase();
    return API_LOOKUP_SOURCES.has(src);
  };

  // Handle Employee ID lookup in edit modal
  const handleEmployeeIdLookup = async (employeeId) => {
    if (!employeeId) return;

    // Only call the API for supply sources that have a backend pool.
    // For other sources (lateral hire, inter department, etc.) the user fills details manually.
    if (!isApiLookupSource()) return;

    try {
      // Prefer Supply Source from current form; fall back to editing row
      const supplySource = editFormData["Supply Source"] || editingRow?.["Supply Source"] || "";

      const response = await api.get("/employees/getEmployeeById", {
        params: { employeeId, supplySource },
        headers: {
          // send supply source as an additional header so backend can scope the lookup
          "X-Supply-Source": supplySource,
        },
      });

      const data = response?.data || {};
      // If backend returned an error/message without employee details, surface it
      if ((data.message || data.error) && !data.employee_name && !data.employee_id) {
        const msg = data.message || data.error || "Employee is not available in the selected supply source.";
        setEditFormData((prev) => ({
          ...prev,
          "Employee Id": "",
          "Employee Name": "",
          "Grades": "",
          "HR Status": "",
          "Constraint": "",
          "SEF": "",
          "Hacker Rank Score": "",
        }));
        setErrorMsg(msg);
      } else if (data && (data.employee_name || data.employee_id || data.employeeId === 0)) {
        setEditFormData((prev) => ({
          ...prev,
          "Employee Name": data.employee_name || data.employeeName || prev["Employee Name"] || "",
          "Grades": data.grade || prev["Grades"] || "",
          "HR Status": data.hrStatus || data.hr_status || prev["HR Status"] || "",
          "Constraint": data.constraint || prev["Constraint"] || "",
          "SEF": data.sef || prev["SEF"] || "",
          "Hacker Rank Score": data.hacker_rank_score || data.hackerRankScore || prev["Hacker Rank Score"] || "",
        }));
      } else {
        // No useful data returned ΓÇö clear Employee Id + dependent fields and show generic toast
        setEditFormData((prev) => ({
          ...prev,
          "Employee Id": "",
          "Employee Name": "",
          "Grades": "",
          "HR Status": "",
          "Constraint": "",
          "SEF": "",
          "Hacker Rank Score": "",
        }));
        setErrorMsg("Employee is not available in the selected supply source.");
      }
    } catch (error) {
      console.error("Error fetching employee name:", error);
      // Clear Employee Id + dependent fields and surface a friendly toast
      setEditFormData((prev) => ({
        ...prev,
        "Employee Id": "",
        "Employee Name": "",
        "Grades": "",
        "HR Status": "",
        "Constraint": "",
        "SEF": "",
        "Hacker Rank Score": "",
      }));
      const msg = (error && error.response && (error.response.data?.message || error.response.data?.error)) || error.message || "Unable to lookup employee for the selected source. Please try again.";
      setErrorMsg(msg);
    }
  };

  // Handle save from edit modal
  const handleEditModalSave = async () => {
    // options: { forceSave: boolean } - when true, skip certain validations (used for Release Associate)
    const opts = arguments[0] || {};
    const forceSave = !!opts.forceSave;
    const overrides = opts.overrides || {};

    if (!editingRow) return;

    // Merge editingRow and current form state, then apply explicit overrides.
    // Using this ordering ensures overrides take precedence even if setState hasn't flushed.
    const mergedRow = { ...editingRow, ...editFormData };
    const newRow = { ...mergedRow, ...overrides };
    
    // Validation: If Fulfilment Plan requires employee info
    if (
      newRow["Fulfilment Plan"] &&
      (
        newRow["Fulfilment Plan"].toLowerCase() === "identified genc" ||
        newRow["Fulfilment Plan"].toLowerCase() === "identified fte" ||
        newRow["Fulfilment Plan"].toLowerCase() === "cwr conversion" ||
        (newRow["Fulfilment Plan"].toLowerCase() === "identified hiring in progress" && newRow["Off Onsite"]?.toLowerCase() === "offshore")
        ) && (
          !newRow["Employee Id"] ||
          !newRow["Employee Name"]
        )
      ) {
      if (!forceSave) {
        setValidationMsg(
          `${editableHeaderMap["Employee Id"] || "Employee Id"} and ${editableHeaderMap["Employee Name"] || "Employee Name"} are mandatory for selected Fulfilment Plan`
        );
        setValidationOpen(true);
        return;
      }
      // If forceSave is true, allow saving even when employee fields are empty (Release flow)
    } else {
      // continue
    }
    
    if (
      newRow["Fulfilment Plan"] &&
      newRow["Fulfilment Plan"].toLowerCase() === "identified hiring in progress" && 
      newRow["Off Onsite"]?.toLowerCase() === "onsite" &&
      !newRow["Employee Name"]
    ) {
      if (!forceSave) {
        setValidationMsg(
          `${editableHeaderMap["Employee Name"] || "Employee Name"} is mandatory for selected Fulfilment Plan`
        );
        setValidationOpen(true);
        return;
      }
      // else allow save during release
    }

    // Rotation SO is always optional – no validation needed

    // Validation: For Rejected / Release From SO actions with specific supply sources,
    // Employee Id and Employee Name are mandatory so we know WHO is being rejected/released.
    const RELEASE_REJECT_SOURCES = new Set(["pdp", "c-deploy", "rotation wo backfill", "rotation w backfill", "project nbl"]);
    const actionLower = String(newRow["Action"] || "").trim().toLowerCase();
    const supplyLower = String(newRow["Supply Source"] || "").trim().toLowerCase();
    if (
      !forceSave &&
      (actionLower === "rejected" || actionLower === "release from so") &&
      RELEASE_REJECT_SOURCES.has(supplyLower) &&
      (!newRow["Employee Id"] || String(newRow["Employee Id"]).trim() === "" ||
       !newRow["Employee Name"] || String(newRow["Employee Name"]).trim() === "")
    ) {
      const errs = {};
      if (!newRow["Employee Id"] || String(newRow["Employee Id"]).trim() === "") errs["Employee Id"] = true;
      if (!newRow["Employee Name"] || String(newRow["Employee Name"]).trim() === "") errs["Employee Name"] = true;
      setEditFieldErrors(errs);
      setErrorMsg("Employee Id and Employee Name are mandatory for Rejected / Release From SO actions.");
      return;
    }

    // Validation: Action Date is mandatory
    if (!forceSave && (!newRow["Action Date"] || String(newRow["Action Date"]).trim() === "")) {
      setValidationMsg("Action Date is mandatory.");
      setValidationOpen(true);
      return;
    }

    // Remarks Details is optional ΓÇô no validation needed

    // Default Allocation Date to today if missing and Demand Status isn't 'to be cancelled'
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      if (!forceSave && newRow["Demand Status"]?.toLowerCase() !== "to be cancelled" && (!newRow["Allocation Date"] || String(newRow["Allocation Date"]).trim() === "")) {
        newRow["Allocation Date"] = todayStr;
      }
    } catch (e) {}

    setEditSaving(true);
    
    try {
      const payload = {
        SoId: newRow["SO Id"],
        SOLineStatus: newRow["SO Line Status"] || "",
        // When computing payload fields, prefer explicit overrides if provided
        ...editableColumns.reduce((acc, col) => {
          if (overrides && Object.prototype.hasOwnProperty.call(overrides, col)) {
            acc[col] = overrides[col];
          } else {
            acc[col] = newRow[col];
          }
          return acc;
        }, {}),
      };

      // Ensure fields required by the backend stored procedure are always present
      // (these may not be in editableColumns but the server expects them)
      if (payload["Supply Account"] === undefined) payload["Supply Account"] = newRow["Supply Account"] || "";
      if (payload["Eff Month"] === undefined) payload["Eff Month"] = newRow["Eff Month"] || "";
      if (payload["Allocation Week"] === undefined) payload["Allocation Week"] = newRow["Allocation Week"] || "";

      // Ensure Employee-specific fields are explicitly set from overrides (defensive - even if not present in editableColumns)
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, "Employee Id")) {
        payload["Employee Id"] = overrides["Employee Id"];
      } else if (newRow["Employee Id"] !== undefined) {
        payload["Employee Id"] = newRow["Employee Id"];
      }
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, "Employee Name")) {
        payload["Employee Name"] = overrides["Employee Name"];
      } else if (newRow["Employee Name"] !== undefined) {
        payload["Employee Name"] = newRow["Employee Name"];
      }
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, "Grades")) {
        payload["Grades"] = overrides["Grades"];
      } else if (newRow["Grades"] !== undefined) {
        payload["Grades"] = newRow["Grades"];
      }

      // Defensive: clear legacy/alias employee keys when releasing
      // Clear known legacy/alias employee keys when releasing to avoid stale values
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, "Employee Id") && overrides["Employee Id"] === "") {
        payload["Identified Assoc Id Ext Candidate Id"] = "";
      }
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, "Employee Name") && overrides["Employee Name"] === "") {
        payload["Identified Assoc Name"] = "";
        payload["Identified Assoc"] = "";
      }

      // Build audit payload (must retain employee information)
      const auditPayload = { ...payload };
      const actionVal = String(auditPayload["Action"] || "").trim().toLowerCase();
      const shouldClearEmployeeInUpdate = actionVal === "rejected" || actionVal === "release from so";

      // Build update payload (clear employee fields only for Rejected/Released)
      const updatePayload = shouldClearEmployeeInUpdate
        ? {
            ...payload,
            "Employee Id": "",
            "Employee Name": "",
            "Grades": "",
          }
        : { ...payload };

      // Debug: log payloads to verify sequence/transform
      try {
        console.debug("Audit payload:", auditPayload);
        console.debug("Update payload:", updatePayload);
      } catch (e) {}

      const token = localStorage.getItem("token");
      await api.post("/demand/audit_insert", auditPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await api.post("/demand/update", updatePayload);

      // Update the data state. Use mergedRow + overrides to ensure the cleared fields are set.
      const rowToSave = { ...mergedRow, ...overrides };
      if (shouldClearEmployeeInUpdate) {
        rowToSave["Employee Id"] = "";
        rowToSave["Employee Name"] = "";
        rowToSave["Grades"] = "";
      }
      const rowUpdater = (prevData) =>
        prevData.map((row) => {
          if (row.item_id === rowToSave.item_id) {
            // Update AllocationWeek if JoiningAllocationDate changed
            if (
              rowToSave["Allocation Date"] &&
              row["Allocation Date"] !== rowToSave["Allocation Date"]
            ) {
              const joiningDate = new Date(rowToSave["Allocation Date"]);
              const weekNumber = getWeekOfMonth(joiningDate);
              const monthName = joiningDate.toLocaleString("default", {
                month: "short",
              });
              rowToSave["Allocation Week"] = `${monthName}-Week ${weekNumber}`;
              const monthNameEff = joiningDate.toLocaleString("default", { month: "short" });
              rowToSave["Eff Month"] = `${monthNameEff}-${joiningDate.getFullYear()}`;
            }
            return { ...row, ...rowToSave };
          }
          return row;
        });
      setData(rowUpdater);
      setInitialData(rowUpdater);

      setEditModalOpen(false);
      setEditingRow(null);
      setEditFormData({});
      setEditFieldErrors({});
      console.log(`Row saved successfully.`);
    } catch (error) {
      console.error("Error saving row:", error);
    } finally {
      setEditSaving(false);
    }
  };

  // Close edit modal
  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({});
    setEditFieldErrors({});
  };

  const handleSaveClick = useCallback(
    (id) => async () => {

      const updatedRow = data.find((row) => row.item_id === id);
      const payload = {
        SoId: updatedRow["SO Id"],
        SOLineStatus: updatedRow["SO Line Status"] || "",
        ...editableColumns.reduce((acc, col) => {
          acc[col] = updatedRow[col];
          return acc;
        }, {}),
      };

      // Ensure fields required by the backend stored procedure are always present
      if (payload["Supply Account"] === undefined) payload["Supply Account"] = updatedRow["Supply Account"] || "";
      if (payload["Eff Month"] === undefined) payload["Eff Month"] = updatedRow["Eff Month"] || "";
      if (payload["Allocation Week"] === undefined) payload["Allocation Week"] = updatedRow["Allocation Week"] || "";

      try {
        const auditPayload = { ...payload };
        const actionVal = String(auditPayload["Action"] || "").trim().toLowerCase();
        const shouldClearEmployeeInUpdate = actionVal === "rejected" || actionVal === "release from so";
        const updatePayload = shouldClearEmployeeInUpdate
          ? {
              ...payload,
              "Employee Id": "",
              "Employee Name": "",
              "Grades": "",
            }
          : { ...payload };

        const token = localStorage.getItem("token");
        await api.post("/demand/audit_insert", auditPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        await api.post("/demand/update", updatePayload);

        if (shouldClearEmployeeInUpdate) {
          const clearUpdater = (prevData) =>
            prevData.map((row) =>
              row.item_id === id
                ? { ...row, "Employee Id": "", "Employee Name": "", "Grades": "" }
                : row
            );
          setData(clearUpdater);
          setInitialData(clearUpdater);
        }

        setRowModesModel((prevModel) => ({
          ...prevModel,
          [id]: { mode: "view" },
        }));
        console.log(`Row with id ${id} saved successfully.`);
      } catch (error) {
        console.error("Error saving row:", error);
      }
    },
    [data, editableColumns, pdlName, setRowModesModel]
  );

  const handleAuditClick = (id) => async () => {
    const row = data.find((r) => r.item_id === id);
    if (!row) return;
    const uniqueId = row["SO Id"];
    setSelectedUnique(uniqueId);
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const response = await api.get("/demand/audit_history", {
        params: { unique_id: uniqueId },
      });
      setAuditData(response.data);
    } catch (error) {
      console.error("Error fetching audit history:", error);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditClose = () => {
    setAuditOpen(false);
    setAuditData([]);
    setSelectedUnique(null);
  };

  const handleCancelClick = useCallback(
    (id) => () => {
      setRowModesModel((prevModel) => ({
        ...prevModel,
        [id]: { mode: "view", ignoreModifications: true },
      }));

      const editedRow = data.find((row) => row.item_id === id);
      if (editedRow?.isNew) {
        setData(data.filter((row) => row.item_id !== id));
      }
    },
    [data, setRowModesModel]
  );

  const filteredRows = useMemo(() => {
    let filteredData = data;

    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      filteredData = filteredData.filter((row) =>
        columns
          .slice(0, -1)
          .some((column) =>
            String(row[column.field]).toLowerCase().includes(lowerSearchText)
          )
      );
    }
    return filteredData;
  }, [data, columns, searchText]);

  const processRowUpdate = async (newRow) => {
    // Validation: If Fulfilment Plan is not "Open", check required fields
    if (
      newRow["Fulfilment Plan"] &&
      (
        newRow["Fulfilment Plan"].toLowerCase() === "identified genc" ||
        newRow["Fulfilment Plan"].toLowerCase() === "identified fte" ||
        newRow["Fulfilment Plan"].toLowerCase() === "cwr conversion" ||
        (newRow["Fulfilment Plan"].toLowerCase() === "identified hiring in progress" && newRow["Off Onsite"].toLowerCase() === "offshore")
      ) &&
      (
        !newRow["Employee Id"] ||
        !newRow["Employee Name"]
      )
    ) {
      setValidationMsg(
        `${editableHeaderMap["Employee Id"] || "Employee Id"} and ${editableHeaderMap["Employee Name"] || "Employee Name"} are mandatory for selected Fulfilment Plan`
      );
      setValidationOpen(true);
      lastEditRowId.current = newRow.item_id; // <-- Store the id
      throw new Error("Validation failed");
    }
    else if (
      newRow["Fulfilment Plan"] &&
      (
        newRow["Fulfilment Plan"].toLowerCase() === "identified hiring in progress" && newRow["Off Onsite"].toLowerCase() === "onsite"
      ) &&
      (
        !newRow["Employee Name"]
      )
    ) {
      setValidationMsg(
        `${editableHeaderMap["Employee Name"] || "Employee Name"} is mandatory for selected Fulfilment Plan`
      );
      setValidationOpen(true);
      lastEditRowId.current = newRow.item_id; // <-- Store the id
      throw new Error("Validation failed");
    }

    // Rotation SO is always optional ΓÇô no validation needed

    // Default Allocation Date to today if missing and Demand Status isn't 'to be cancelled' (inline edit)
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      if (newRow["Demand Status"]?.toLowerCase() !== "to be cancelled" && (!newRow["Allocation Date"] || String(newRow["Allocation Date"]).trim() === "")) {
        newRow["Allocation Date"] = todayStr;
      }
    } catch (e) {}

    const updatedRow = { ...newRow, isNew: false };
    try {
      const payload = {
        SoId: updatedRow["SO Id"],
        SOLineStatus: updatedRow["SO Line Status"] || "",
        ...editableColumns.reduce((acc, col) => {
          acc[col] = updatedRow[col];
          return acc;
        }, {}),
      };

      // Ensure fields required by the backend stored procedure are always present
      if (payload["Supply Account"] === undefined) payload["Supply Account"] = updatedRow["Supply Account"] || "";
      if (payload["Eff Month"] === undefined) payload["Eff Month"] = updatedRow["Eff Month"] || "";
      if (payload["Allocation Week"] === undefined) payload["Allocation Week"] = updatedRow["Allocation Week"] || "";

      const auditPayload = { ...payload };
      const actionVal = String(auditPayload["Action"] || "").trim().toLowerCase();
      const shouldClearEmployeeInUpdate = actionVal === "rejected" || actionVal === "release from so";
      const updatePayload = shouldClearEmployeeInUpdate
        ? {
            ...payload,
            "Employee Id": "",
            "Employee Name": "",
            "Grades": "",
          }
        : { ...payload };

      const token = localStorage.getItem("token");
      console.log("Token:", token);
      await api.post("/demand/audit_insert", auditPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await api.post("/demand/update", updatePayload);

      console.log(`Row with id ${updatedRow.item_id} saved successfully.`);

      if (shouldClearEmployeeInUpdate) {
        updatedRow["Employee Id"] = "";
        updatedRow["Employee Name"] = "";
        updatedRow["Grades"] = "";
      }

      const inlineUpdater = (prevData) =>
        prevData.map((row) => {
          if (row.item_id === updatedRow.item_id) {
            // Find the old row to compare JoiningAllocationDate
            const oldRow = prevData.find(
              (r) => r.item_id === updatedRow.item_id
            );
            // Update AllocationWeek only if JoiningAllocationDate has changed
            if (
              updatedRow.JoiningAllocationDate &&
              oldRow.JoiningAllocationDate !== updatedRow.JoiningAllocationDate
            ) {
              const joiningDate = new Date(updatedRow.JoiningAllocationDate);
              const weekNumber = getWeekOfMonth(joiningDate);
              const monthName = joiningDate.toLocaleString("default", {
                month: "short",
              });
              updatedRow.AllocationWeek = `${monthName}-Week ${weekNumber}`;
              updatedRow.JoiningAllocationDate = joiningDate
                .toISOString()
                .split("T")[0];
            }

            return updatedRow;
          }
          return row;
        });
      setData(inlineUpdater);
      setInitialData(inlineUpdater);
      lastEditRowId.current = null;
    } catch (error) {
      console.error("Error saving row:", error);
      throw error;
    }
    return updatedRow;
  };

  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const getRowClassName = (params) => {
    return rowModesModel[params.id]?.mode === "edit" ? "row-editing" : "";
  };

  const handleParentCustomerChange = (event) => {
    setParentCustomer(event.target.value);
  };

  const handleBuDescChange = (event) => {
    setBuDesc(event.target.value);
  };

  const handlePdlNameChange = (event) => {
    setPdlName(event.target.value);
  };

  const handleOffOnChange = (event) => {
    setOffOn(event.target.value);
  };

  const handleViewReport = async () => {
    try {
      const params = {
        parentCustomer: parentCustomer === "All" ? "null" : parentCustomer,
        buDesc: buDesc === "All" ? "null" : buDesc,
        pdlName: pdlName === "All" ? "null" : pdlName,
        offOn: offOn === "All" ? "null" : offOn,
      };

      const response = await api.get("/demandselect/report", { params });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching report data:", error);
    }
  };

  // State for Recommendations Modal
  const [sampleData, setSampleData] = useState([]);
  const allKnownSources = ["PDP", "VCDP", "Rotation", "Lateral Hire", "NBL"];
  const availableSourcesLower = useMemo(() => {
    const s = new Set();
    (sampleData || []).forEach((r) => {
      const src = (r && (r.source || r.sourceId || r.sourceName)) ? String(r.source || r.sourceId || r.sourceName) : "";
      if (src) s.add(src.toLowerCase());
    });
    return s;
  }, [sampleData]);
  const [chartView, setChartView] = useState(() => {
    const saved = localStorage.getItem("demandChartView");
    return saved === "true";
  });
  const [loading, setLoading] = useState(() => {
    // If chartView is enabled, don't show loading initially
    const savedChartView = localStorage.getItem("demandChartView");
    return savedChartView !== "true";
  });
  const [showTour, setShowTour] = useState(() => {
    const savedChartView = localStorage.getItem("demandChartView");
    const tourSeen = localStorage.getItem("demandTourSeen");
    return savedChartView !== "true" && tourSeen !== "true";
  });
  const toggleRef = useRef(null);

  // Auto-close tour after 5 seconds
  useEffect(() => {
    if (showTour) {
      const timer = setTimeout(() => {
        setShowTour(false);
        localStorage.setItem("demandTourSeen", "true");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showTour]);

  const closeTour = () => {
    setShowTour(false);
    localStorage.setItem("demandTourSeen", "true");
  };

  const [errorMsg, setErrorMsg] = useState("");
  // Listen for global employee lookup errors dispatched by helper edit-cells
  useEffect(() => {
    const handler = (e) => {
      try {
        const msg = e && e.detail ? e.detail : "Employee lookup failed";
        setErrorMsg(msg);
      } catch (err) {
        setErrorMsg("Employee lookup failed");
      }
    };
    window.addEventListener('employeeLookupError', handler);
    return () => window.removeEventListener('employeeLookupError', handler);
  }, []);
  const handleChartViewToggle = (event) => {
    const newValue = event.target.checked;
    setChartView(newValue);
    localStorage.setItem("demandChartView", newValue.toString());
    // When switching to chart view, hide the loading
    if (newValue) {
      setLoading(false);
    }
  };
  const [sameCity, setSameCity] = useState(false);
  const [soId, setSoId] = useState("");
  const [skillRequired, setSkillRequired] = useState("");
  const [requirementStartDate, setRequirementStartDate] = useState("");
  const [soGeo, setSoGeo] = useState("India");
  const [city, setCity] = useState("");
  const [soGrade, setSoGrade] = useState("");
  const [accountName, setAccountName] = useState("");
  const [grade, setGrade] = useState("All Grades");
  const [sources, setSources] = useState([
    "PDP",
    "NBL",
    "VCDP",
    "Lateral Hire",
    "Rotation",
  ]);
  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null); // Selected employee in Resource Lookup
  const [mapBlink, setMapBlink] = useState(false);
  const [currentDemandRow, setCurrentDemandRow] = useState(null); // Current demand row for tagging
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedEmployee(null); // Clear selection when closing
  };

  const handleGradeChange = (event) => {
    setGrade(event.target.value);
  };

  // Trigger a 1s blink on the Map button whenever the selected employee changes
  useEffect(() => {
    if (selectedEmployee) {
      setMapBlink(true);
      const t = setTimeout(() => setMapBlink(false), 1000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [selectedEmployee]);

  const handleSourceChange = (event) => {
    const { checked, name } = event.target;
    setSources((prev) =>
      checked ? [...prev, name] : prev.filter((s) => s !== name)
    );
  };

  // Handle Tag button click - open edit modal with selected employee data
  const handleTagClick = () => {
    if (!selectedEmployee || !currentDemandRow) return;
    
    // Close the Resource Lookup modal
    setOpen(false);
    
    // Open edit modal with current demand row
    setEditingRow(currentDemandRow);
    
    // Initialize form data with current row values and selected employee data
    const formData = {};
    editableColumns.forEach((col) => {
      formData[col] = currentDemandRow[col] || "";
    });
    
    // Populate with selected employee data
    formData["Employee Id"] = selectedEmployee.employeeId || "";
    formData["Employee Name"] = selectedEmployee.employeeName || "";
    formData["Grades"] = selectedEmployee.grade || "";

    // Normalize Included In Forecast from currentDemandRow if present
    try {
      if ((formData["Included In Forecast"] || currentDemandRow["Included In Forecast"])) {
        const rawVal = formData["Included In Forecast"] || currentDemandRow["Included In Forecast"];
        formData["Included In Forecast"] = mapYesNoToOption(rawVal);
      }
    } catch (e) {}

    // Auto-fill Supply Source based on recommendation source (robust to different property names)
    try {
      const srcVal = (selectedEmployee.source || selectedEmployee.sourceId || selectedEmployee.sourceName || selectedEmployee.sourceType || "").toString().toLowerCase();
      let supplySourceLabel = "";
      if (srcVal) {
        if (srcVal.includes("pdp")) {
          supplySourceLabel = "PDP";
        } else if (srcVal.includes("vcdp") || srcVal.includes("vdcp") || srcVal.includes("c-deploy") || srcVal.includes("c deploy")) {
          supplySourceLabel = "C-Deploy";
        } else if (srcVal.includes("nbl")) {
          supplySourceLabel = "Project NBL";
        }
      }
      // If a label was determined, try to find the corresponding dropdown value from dropdownOptions
      if (supplySourceLabel) {
        const opts = dropdownOptions["Supply Source"] || [];
        const match = opts.find(o => (o.label || "").toString().toLowerCase() === supplySourceLabel.toLowerCase() || (o.value || "").toString().toLowerCase() === supplySourceLabel.toLowerCase());
        if (match) {
          formData["Supply Source"] = match.value;
        } else {
          // fallback to label if no matching value found
          formData["Supply Source"] = supplySourceLabel;
        }
      }
    } catch (e) {
      // ignore mapping errors
    }

    // Proposed Date is read-only; it auto-populates from table data (no default to today)
    const todayStr = new Date().toISOString().split("T")[0];

    // Default Allocation Date to today if empty (for Tag flow)
    if ((!formData["Allocation Date"] || String(formData["Allocation Date"]).trim() === "") && (!formData["Demand Status"] || String(formData["Demand Status"]).toLowerCase() !== "to be cancelled")) {
      formData["Allocation Date"] = todayStr;
    }

    // Default Action Date to today if empty (for Tag flow)
    if (!formData["Action Date"] || String(formData["Action Date"]).trim() === "") {
      formData["Action Date"] = todayStr;
    }

    // Default SO Reserve Status from persisted default if empty
    try {
      const defaultReserve = localStorage.getItem("defaultSoReserveStatus");
      if ((!formData["SO Reserve Status"] || String(formData["SO Reserve Status"]).trim() === "") && defaultReserve) {
        formData["SO Reserve Status"] = defaultReserve;
      }
    } catch (e) {}
    
    setEditFormData(formData);
    setEditModalOpen(true);
    
    // Clear selection
    setSelectedEmployee(null);
  };

  // Build a set of employeeIds already present in the main demand table to disable duplicates in recommendations.
  const existingEmployeeIds = useMemo(() => {
    const set = new Set();
    (filteredRows || []).forEach((r) => {
      const id = r["Employee Id"] || r["EmployeeId"] || r["employeeId"] || r["Emp Id"] || r["EmpId"];
      if (id !== undefined && id !== null && String(id).trim() !== "") set.add(String(id).trim().toLowerCase());
    });
    return set;
  }, [filteredRows]);

  // Filter client-side for recommendations. Avoid side-effects here for performance.
  const filteredDataRec = useMemo(() => {
    const seen = new Set();
    const rows = (sampleData || [])
      .filter((row) => {
        const gradeMatch = grade === "All Grades" || row.grade === grade;
        const sourceMatch = sources.includes(row.source);
        const cityMatch = !sameCity || (String(row.location || "").toLowerCase().includes(String(city || "").toLowerCase()));
        return gradeMatch && sourceMatch && cityMatch;
      })
      .map((row) => ({ ...row, location: (row.location === undefined || String(row.location).includes("undefined")) ? "NA" : row.location }));

    const distinct = rows.filter((row) => {
      const empRaw = row.employeeId || row["Employee Id"] || row.employeeName || "";
      const empKey = String(empRaw).trim().toLowerCase();
      // If no empKey, fallback to composite key of name+grade+source+location
      const key = empKey || `${String(row.employeeName||"").trim().toLowerCase()}::${String(row.grade||"").trim().toLowerCase()}::${String(row.source||"").trim().toLowerCase()}::${String(row.location||"").trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Move already-associated employees (greyed) to the end
    distinct.sort((a, b) => {
      const aId = String(a.employeeId || a["Employee Id"] || "").trim().toLowerCase();
      const bId = String(b.employeeId || b["Employee Id"] || "").trim().toLowerCase();
      const aExists = aId && existingEmployeeIds.has(aId);
      const bExists = bId && existingEmployeeIds.has(bId);
      if (aExists === bExists) return 0;
      return aExists ? 1 : -1;
    });

    return distinct;
  }, [sampleData, grade, sources, sameCity, city, existingEmployeeIds]);

  // Columns for the recommendation DataGrid memoized to avoid re-creating on each render
  const recColumns = useMemo(() => [
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const empId = params.row?.employeeId ? String(params.row.employeeId).trim() : "";
        const disabled = existingEmployeeIds.has(empId);
        return (
          <input
            type="radio"
            name="employeeSelect"
            checked={selectedEmployee?.employeeId === params.row.employeeId}
            onChange={() => !disabled && setSelectedEmployee(params.row)}
            style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            disabled={disabled}
          />
        );
      },
    },
    { field: "employeeId", headerName: "Employee ID", width: 120 },
    { field: "employeeName", headerName: "Employee Name", width: 180 },
    { field: "grade", headerName: "Grade", width: 120 },
    { field: "location", headerName: "Location", width: 150 },
    { field: "sourceId", headerName: "Source ID", width: 120 },
    { field: "source", headerName: "Source", width: 150 },
    { field: "availableFromDate", headerName: "Available From Date", width: 180 },
    { field: "primarySkill", headerName: "Primary Skill", width: 150 },
    { field: "secondarySkills", headerName: "Secondary Skills", width: 180 },
      { field: "SEF", headerName: "SEF", width: 120 },
       { field: "HR Status", headerName: "HR Status", width: 120 },
    // { field: "skillMatchPercentage", headerName: "Skill Match(%)", width: 180 }, // Hidden per requirements
  ], [existingEmployeeIds, selectedEmployee]);

  // Merge Fulfilment Plan options: server-provided dropdownOptions + distinct values seen in parent demand table
  const fulfilmentPlanOptions = useMemo(() => {
    const serverOpts = (dropdownOptions["Fulfilment Plan"] || []).map(o => ({ value: o.value, label: o.label }));
    const seen = new Set(serverOpts.map(o => String(o.value || o.label || "").trim()));
    const parentValues = new Set();
    (initialData || data || []).forEach(r => {
      const v = r["Fulfilment Plan"] || r["FulfilmentPlan"] || r["Fulfilment plan"] || r["Fulfilment"] || "";
      if (v !== undefined && v !== null && String(v).trim() !== "") parentValues.add(String(v).trim());
    });
    parentValues.forEach(v => {
      if (!seen.has(v)) {
        serverOpts.push({ value: v, label: v });
        seen.add(v);
      }
    });
    // Ensure current editFormData value is included
    const current = editFormData["Fulfilment Plan"] || editFormData["FulfilmentPlan"] || "";
    if (current && !seen.has(String(current).trim())) {
      serverOpts.unshift({ value: current, label: current });
    }
    return serverOpts;
  }, [dropdownOptions, initialData, data, editFormData]);

  const handleRecommendationsClick = useCallback(async (row) => {
    setLoading(true);
    setCurrentDemandRow(row); // Store the current demand row for tagging
    setSelectedEmployee(null); // Clear any previous selection
    const getField = (obj, candidates) => {
      for (const k of candidates) {
        if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) return obj[k];
      }
      // try case-insensitive match
      const lowerMap = Object.keys(obj).reduce((m, key) => { m[key.toLowerCase().trim()] = key; return m; }, {});
      for (const k of candidates) {
        const found = lowerMap[String(k).toLowerCase().trim()];
        if (found && obj[found] !== undefined && obj[found] !== null) return obj[found];
      }
      return undefined;
    };

    setSoId(getField(row, ["SO Id", "So Id", "so id", "soId"]) || "");
    setSkillRequired(getField(row, ["Technical Skills Required", "Technical Skills", "TechnicalSkillsRequired"]) || "");
    const reqDate = getField(row, ["Requirement Start Date", "RequirementStartDate", "requirement start date", "Requirement Startdate", "Requirement Start Date "]);
    setRequirementStartDate(reqDate != null ? reqDate : "");
    setSoGeo(getField(row, ["Geography", "geography"]) ? getField(row, ["Geography", "geography"]) : "India");
    setCity(getField(row, ["City", "city"]) || "");
    setSoGrade(getField(row, ["So Grade", "SO Grade", "SoGrade", "soGrade"]) || "");
    setAccountName(getField(row, ["Account Name", "AccountName"]) || "");

    const body = {
      soId: row["SO Id"],
      skillRequired: row["Technical Skills Required"],
      requirementStartDate: row["Requirement Start Date"] != null ? row["Requirement Start Date"] : "",
      soGeo: row["Geography"] ? row["Geography"] : "India",
      skillTower: getField(row, ["Skill Tower", "SkillTower", "skill tower", "skillTower"]) || "",
      maxCount: 10
    };

    try {
      const response = await api.post("/api/pool/recommend", body);
      setSampleData(response.data.finalOutput || []);
      setOpen(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      setLoading(false);
    }
  }, []);

  const columnsWithActions = useMemo(
    () => {
      return [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 120,
        cellClassName: "actions",
        getActions: ({ id, row }) => {
          // Maintenance window check for Edit and Recommendation icons
          const isMaint = underMaintenance;

          return [
            <Tooltip title={isMaint ? MAINTENANCE_TOOLTIP : "Edit"}>
              <span>
                <GridActionsCellItem
                  icon={<EditIcon sx={isMaint ? { opacity: 0.4 } : { color: '#000' }} />}
                  label={isMaint ? MAINTENANCE_TOOLTIP : "Edit"}
                  onClick={isMaint ? undefined : handleEditClick(id)}
                  color="inherit"
                  disabled={isMaint}
                />
              </span>
            </Tooltip>,
            <GridActionsCellItem
              icon={<HistoryIcon sx={{ color: '#000' }} />}
              label="Audit History"
              onClick={handleAuditClick(id)}
              color="inherit"
            />,
            <Tooltip title={isMaint ? MAINTENANCE_TOOLTIP : "Resource Lookup"}>
              <span>
                <GridActionsCellItem
                  icon={
                    isMaint
                      ? <PersonSearchIcon sx={{ opacity: 0.4 }} />
                      : ((row["Employee Id"] || row["Employee Name"]) ? (
                          <HowToRegIcon sx={{ color: "green" }} />
                        ) : (
                          <PersonSearchIcon sx={{ color: "red" }} />
                        ))
                  }
                  label={isMaint ? MAINTENANCE_TOOLTIP : "Resource Lookup"}
                  onClick={() => !isMaint && handleRecommendationsClick(row)}
                  color="primary"
                  disabled={isMaint}
                  showInMenu={false}
                />
              </span>
            </Tooltip>,
          ];
        },
      },
      ...columns.map((col) => ({
        ...col,
        editable: false,
        cellClassName: editableColumns.includes(col.field)
          ? "editable-cell"
          : null,
      })),
    ];},
    [
      columns,
      editableColumns,
      dropdownOptions,
      handleRecommendationsClick,
      underMaintenance,
    ]
  );

  return (
    <Box sx={{ width: "100%" }}>

      <>


      {/* Loader */}
      <Backdrop open={loading || !configReady} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading Demand View...
          </Typography>
        </Box>
      </Backdrop>

      {/* Error Toast */}
      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditModalClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { width: '90%', height: '75%', maxWidth: '90%' } }}
      >
        <DialogTitle sx={{ backgroundColor: primaryColor, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">
            Edit Demand - SO Id: {editingRow?.["SO Id"]} [ Account Name: {editingRow?.["Account Name"]} - Project Name: {editingRow?.["Project Name"]}]
          </Typography>
          <IconButton onClick={handleEditModalClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 1, px: 1, height: 'calc(100% - 140px)', overflow: 'auto', '& .MuiOutlinedInput-input': { padding: '8px 10px' }, '& .MuiSelect-select': { padding: '8px 10px' } }}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {editableMeta.filter((m) => m.apiField !== "Allocation Week").map(({ apiField, headerName }) => (
              <Grid item xs={12} sm={6} md={3} key={apiField} sx={{ px: 1, py: 1 }}>
                {(() => {
                  const required = isFieldRequired(apiField);
                  const valueEmpty = !editFormData[apiField] || String(editFormData[apiField]).trim() === "";
                  const showError = required && valueEmpty;

                  if (fieldToDropdownTypeMap[apiField]) {
                    // border color: blue when required (per UX) ΓÇö use primaryColor
                    const selectBorderColor = required ? primaryColor : undefined;
                    return (
                      <FormControl fullWidth size="small" required={required} error={false} sx={selectBorderColor ? {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: selectBorderColor },
                          '&:hover fieldset': { borderColor: selectBorderColor },
                          '&.Mui-focused fieldset': { borderColor: selectBorderColor },
                        }
                      } : {}}>
                        <InputLabel>{(headerName || apiField) + (required ? ' *' : '')}</InputLabel>
                        <Select
                          value={editFormData[apiField] || ""}
                          label={(headerName || apiField) + (required ? ' *' : '')}
                          onChange={(e) => apiField === "Supply Source" ? handleSupplySourceSelectChange(e.target.value) : apiField === "Demand Status" ? handleDemandStatusChange(e.target.value) : handleEditFormChange(apiField, e.target.value)}
                        >
                          {((apiField === "Fulfilment Plan") ? fulfilmentPlanOptions : (dropdownOptions[apiField] || [])).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }

                  if (apiField === "Allocation Date") {
                      // Allocation Date is required unless Demand Status is 'to be cancelled'
                      const demandStatus = (editFormData["Demand Status"] || editingRow?.["Demand Status"] || "").toString().toLowerCase();
                      const requiredNow = demandStatus !== "to be cancelled";
                      const allocEmpty = !editFormData[apiField] || String(editFormData[apiField]).trim() === "";
                      const showAllocError = requiredNow && allocEmpty;
                      return (
                        <TextField
                          fullWidth
                          size="small"
                          label={(headerName || apiField) + (requiredNow ? ' *' : '')}
                          required={requiredNow}
                          error={showAllocError}
                          helperText={showAllocError ? "Allocation Date is mandatory." : ""}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: showAllocError ? '#d32f2f' : (required ? primaryColor : undefined) },
                              '&:hover fieldset': { borderColor: showAllocError ? '#d32f2f' : (required ? primaryColor : undefined) },
                              '&.Mui-focused fieldset': { borderColor: showAllocError ? '#d32f2f' : (required ? primaryColor : undefined) },
                            },
                          }}
                          type="date"
                          value={editFormData[apiField] || ""}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            handleEditFormChange(apiField, newDate);
                            if (newDate) {
                              const dateObj = new Date(newDate);
                              const weekNumber = getWeekOfMonth(dateObj);
                              const monthName = dateObj.toLocaleString("default", { month: "short" });
                              handleEditFormChange("Allocation Week", `${monthName}-Week ${weekNumber}`);
                              handleEditFormChange("Eff Month", `${monthName}-${dateObj.getFullYear()}`);
                            } else {
                              handleEditFormChange("Allocation Week", "");
                              handleEditFormChange("Eff Month", "");
                            }
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      );
                  }

                  if (apiField === "Employee Id") {
                    const empIdError = !!editFieldErrors["Employee Id"];
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={(headerName || apiField) + (required ? ' *' : '')}
                        required={required}
                        error={empIdError}
                        helperText={empIdError ? "Employee Id is required for this action." : ""}
                        value={editFormData[apiField] || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleEditFormChange(apiField, val);
                          setEditFieldErrors(prev => ({ ...prev, "Employee Id": false }));
                          // If Employee Id is cleared, also clear Employee Name and Grades
                          if (!val || val.trim() === "") {
                            handleEditFormChange("Employee Name", "");
                            handleEditFormChange("Grades", "");
                            handleEditFormChange("HR Status", "");
                            handleEditFormChange("Constraint", "");
                            handleEditFormChange("SEF", "");
                            handleEditFormChange("Hacker Rank Score", "");
                          }
                        }}
                        onBlur={(e) => handleEmployeeIdLookup(e.target.value)}
                        placeholder="Enter Employee ID"
                        sx={empIdError ? {
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                            '&:hover fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                            '&.Mui-focused fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                          }
                        } : (required ? {
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: primaryColor },
                            '&:hover fieldset': { borderColor: primaryColor },
                            '&.Mui-focused fieldset': { borderColor: primaryColor },
                          }
                        } : {})}
                      />
                    );
                  }

                  if (apiField === "Allocation Week" || apiField === "Eff Month") {
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        value={editFormData[apiField] || ""}
                        InputProps={{ readOnly: true }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    );
                  }

                     if (apiField === "Skill Tower") {
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        value={editFormData[apiField] || ""}
                        InputProps={{ readOnly: true }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    );
                  }

                  if (apiField === "HR Status" || apiField === "Constraint" || apiField === "SEF" || apiField === "Hacker Rank Score") {
                    const readOnlyMeta = isApiLookupSource();
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        value={editFormData[apiField] || ""}
                        onChange={readOnlyMeta ? undefined : (e) => handleEditFormChange(apiField, e.target.value)}
                        InputProps={{ readOnly: readOnlyMeta }}
                        placeholder={readOnlyMeta ? undefined : `Enter ${headerName || apiField}`}
                        sx={{ backgroundColor: readOnlyMeta ? "#f5f5f5" : undefined }}
                        helperText={readOnlyMeta ? "Auto-populated from Employee lookup" : undefined}
                      />
                    );
                  }

                  if (apiField === "Employee Name" || apiField === "Grades") {
                    // Read-only when supply source supports API lookup; editable otherwise (lateral, inter-dept, etc.)
                    const readOnlyEmp = isApiLookupSource();
                    const empNameError = apiField === "Employee Name" && !!editFieldErrors["Employee Name"];
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={(headerName || apiField) + (required ? ' *' : '')}
                        required={required}
                        error={empNameError}
                        helperText={empNameError ? "Employee Name is required for this action." : (readOnlyEmp ? "Auto-populated from Employee lookup" : undefined)}
                        value={editFormData[apiField] || ""}
                        onChange={readOnlyEmp ? undefined : (e) => { handleEditFormChange(apiField, e.target.value); if (apiField === "Employee Name") setEditFieldErrors(prev => ({ ...prev, "Employee Name": false })); }}
                        InputProps={{ readOnly: readOnlyEmp }}
                        placeholder={readOnlyEmp ? undefined : `Enter ${headerName || apiField}`}
                        sx={{
                          backgroundColor: readOnlyEmp ? "#f5f5f5" : undefined,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: empNameError ? '#d32f2f' : (required ? primaryColor : undefined), borderWidth: empNameError ? 2 : undefined },
                            '&:hover fieldset': { borderColor: empNameError ? '#d32f2f' : (required ? primaryColor : undefined), borderWidth: empNameError ? 2 : undefined },
                            '&.Mui-focused fieldset': { borderColor: empNameError ? '#d32f2f' : (required ? primaryColor : undefined), borderWidth: empNameError ? 2 : undefined },
                          }
                        }}
                      />
                    );
                  }

                  if (apiField === "SO Reserve Status") {
                    const reserveVal = editFormData[apiField] || "";
                    return (
                      <FormControl fullWidth size="small">
                        <InputLabel>{headerName || apiField}</InputLabel>
                        <Select
                          value={reserveVal}
                          label={headerName || apiField}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleEditFormChange(apiField, v);
                            try { localStorage.setItem("defaultSoReserveStatus", v); } catch (err) {}
                          }}
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    );
                  }

                  if (apiField === "Proposed Date") {
                    // Proposed Allocation Date is read-only; auto-populated from table data
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label="Proposed Allocation Date"
                        type="date"
                        value={editFormData[apiField] || ""}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    );
                  }

                  if (apiField === "Billing Start Date" || apiField === "Reserve End Date") {
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        type="date"
                        value={editFormData[apiField] || ""}
                        onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    );
                  }

                  if (apiField === "Action") {
                    return (
                      <FormControl fullWidth size="small">
                        <InputLabel>{headerName || "Action"}</InputLabel>
                        <Select
                          value={editFormData["Action"] || ""}
                          label={headerName || "Action"}
                          onChange={(e) => handleEditFormChange("Action", e.target.value)}
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {actionDropdownOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }

                  // Action Date is mandatory
                  if (apiField === "Action Date") {
                    const isRequired = true;
                    const isEmpty = !editFormData[apiField] || String(editFormData[apiField]).trim() === "";
                    const showFieldError = isRequired && isEmpty;
                    const actionSelected = !!(editFormData["Action"] && String(editFormData["Action"]).trim() !== "");
                    const blinkSx = actionSelected ? {
                      '@keyframes fieldBlink': {
                        '0%':   { borderColor: '#ff9800' },
                        '50%':  { borderColor: '#ffd54f' },
                        '100%': { borderColor: '#ff9800' },
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: showFieldError ? '#d32f2f' : '#ff9800', borderWidth: 2, animation: 'fieldBlink 1.2s ease-in-out infinite' },
                        '&:hover fieldset': { borderColor: showFieldError ? '#d32f2f' : '#ff9800', borderWidth: 2 },
                        '&.Mui-focused fieldset': { borderColor: showFieldError ? '#d32f2f' : '#ff9800', borderWidth: 2, animation: 'none' },
                      }
                    } : (showFieldError ? {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                        '&:hover fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                        '&.Mui-focused fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                      }
                    } : {});
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={(headerName || apiField) + ' *'}
                        required
                        error={showFieldError}
                        helperText={showFieldError ? "Action Date is mandatory." : ""}
                        type="date"
                        value={editFormData[apiField] || ""}
                        onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={blinkSx}
                      />
                    );
                  }

                  // Remarks Details ΓÇö optional text field
                  if (apiField === "Remarks Details") {
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        value={editFormData[apiField] || ""}
                        onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                      />
                    );
                  }

                  // Default text field (includes Rotation SO and other fields)
                  return (
                      <TextField
                      fullWidth
                      size="small"
                        label={(headerName || apiField) + (required ? ' *' : '')}
                        required={required}
                        error={false}
                      value={editFormData[apiField] || ""}
                      onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                        sx={required ? {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: primaryColor },
                          '&:hover fieldset': { borderColor: primaryColor },
                          '&.Mui-focused fieldset': { borderColor: primaryColor },
                        }
                      } : {}}
                    />
                  );
                })()}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            {/* Release Associate button hidden per requirements */}
          </Box>
          <Button onClick={handleEditModalClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handleEditModalSave()}
            variant="contained"
            color="primary"
            disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {editSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release confirmation dialog (MUI) */}
      <Dialog
        open={releaseConfirmOpen}
        onClose={() => setReleaseConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Release</DialogTitle>
        <DialogContent>
          <Typography>
            Releasing this associate will remove their assignment from this SO and update the supply record so the associate becomes available for other SO recommendations. This action cannot be undone. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={() => {
              // Close confirmation dialog
              setReleaseConfirmOpen(false);
              if (!releaseOverrides) return;

              // Build overrides that clear employee-related fields and known legacy aliases
              const clearOverrides = {
                ...releaseOverrides,
                "Identified Assoc Name": "",
                "Identified Assoc": "",
                "Identified Assoc Id Ext Candidate Id": "",
              };

              // Apply cleared values to the edit modal form and editing row, but DO NOT auto-save.
              // This keeps the edit modal open so the user can review and manually press Save.
              setEditFormData((prev) => ({ ...prev, ...clearOverrides }));
              setEditingRow((prev) => (prev ? { ...prev, ...clearOverrides } : prev));
            }}
            color="secondary"
            variant="contained"
            disabled={editSaving}
          >
            Confirm Release
          </Button>
        </DialogActions>
      </Dialog>

      {sampleData && 
      (
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              height: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ flex: 1 }}>
                Resource Lookup
              </Typography>

              {/* Inline filters placed between title and Map button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mr: 1 }}>
                <TextField select label="Grade Level" value={grade} onChange={handleGradeChange} size="small" sx={{ minWidth: 150 }} >
                  <MenuItem value="All Grades">All Grades</MenuItem>
                  <MenuItem value={soGrade}>Same Grades</MenuItem>
                  <MenuItem value="PAT">PAT</MenuItem>
                  <MenuItem value="PA">PA</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                  <MenuItem value="SA">SA</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="SM">SM</MenuItem>
                  <MenuItem value="AD">AD</MenuItem>
                  <MenuItem value="CWR">CWR</MenuItem>
                </TextField>

                <FormControlLabel
                  control={<Checkbox checked={sameCity} onChange={(e) => setSameCity(e.target.checked)} />}
                  label="Same City"
                />

                <FormGroup row sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {allKnownSources.map((src) => {
                    const disabled = !availableSourcesLower.has(src.toLowerCase());
                    return (
                      <FormControlLabel
                        key={src}
                        control={<Checkbox checked={sources.includes(src)} onChange={handleSourceChange} name={src} disabled={disabled} />}
                        label={src}
                      />
                    );
                  })}
                </FormGroup>

                <Button size="small" variant="outlined" onClick={() => {
                  const opts = allKnownSources.filter(o => availableSourcesLower.has(o.toLowerCase()));
                  const allSelected = opts.length > 0 && opts.every((o) => sources.includes(o));
                  setSources(allSelected ? [] : opts);
                }}>
                  {(allKnownSources.filter(o => availableSourcesLower.has(o.toLowerCase())).every((o) => sources.includes(o))) ? 'Unselect All' : 'Select All'}
                </Button>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleTagClick}
                  disabled={!selectedEmployee}
                  sx={{
                    mr: 1,
                    bgcolor: selectedEmployee ? 'success.main' : undefined,
                    color: selectedEmployee ? '#fff' : undefined,
                    '&:hover': {
                      bgcolor: selectedEmployee ? 'success.dark' : undefined,
                    },
                    ...(mapBlink
                      ? {
                          '@keyframes mapBlink': {
                            '0%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.7)' },
                            '50%': { boxShadow: '0 0 12px 6px rgba(76,175,80,0.45)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0)' },
                          },
                          animation: 'mapBlink 1s ease-in-out',
                        }
                      : {}),
                  }}
                >
                  Map
                </Button>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

      
                  <TableContainer component={Paper} sx={{ mb: 2, maxWidth: "100%" }}>
                    <Table size="small">
                    <TableBody>
                      <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff" }}>SO Id</TableCell>
                      <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>{soId}</TableCell>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff", width: 180 }}>Skills Required</TableCell>
                      <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>{skillRequired}</TableCell>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff", width: 120 }}>RSD</TableCell>
                      <TableCell sx={{ maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>{requirementStartDate}</TableCell>
                      </TableRow>
                      <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff", width: 140 }}>Geography</TableCell>
                      <TableCell sx={{ maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>{soGeo}</TableCell>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff", width: 100 }}>City</TableCell>
                      <TableCell sx={{ maxWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }}>{city}</TableCell>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff", width: 120 }}>SO Grade</TableCell>
                      <TableCell sx={{ maxWidth: 120, whiteSpace: 'normal', wordBreak: 'break-word' }}>{soGrade}</TableCell>
                      </TableRow>
                      {/* <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: "bold", backgroundColor: primaryColor, color: "#fff" }}>Account Name</TableCell>
                      <TableCell colSpan={5}>{accountName}</TableCell>
                      </TableRow> */}
                </TableBody>
              </Table>
            </TableContainer>
        

            {/* DataGrid table */}
            <StyledDataGrid
              rows={filteredDataRec}
              columns={recColumns}
              pageSize={5}
              rowsPerPageOptions={[5, 10]}
              disableSelectionOnClick
              getRowId={(row) => row.employeeId}
              onRowClick={(params) => {
                const empId = params.row?.employeeId ? String(params.row.employeeId).trim() : "";
                if (!existingEmployeeIds.has(empId)) setSelectedEmployee(params.row);
              }}
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#e3f2fd",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#bbdefb !important",
                },
                // style disabled recommendation rows (already present in main demand table)
                "& .disabled-rec-row": {
                  backgroundColor: "#f5f5f5",
                  color: "#9e9e9e",
                },
              }}
              getRowClassName={(params) => {
                const empId = params.row?.employeeId ? String(params.row.employeeId).trim() : "";
                return existingEmployeeIds.has(empId) ? 'disabled-rec-row' : '';
              }}
            />
          </Box>
        </Modal>
  )}
        {/* Confirm dialog when changing Supply Source with employee present */}
        <Dialog
          open={confirmSupplyChangeOpen}
          onClose={cancelSupplyChange}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Supply Source?</DialogTitle>
          <DialogContent>
            <Typography>
              Changing the Supply Source will clear the currently entered associate details (Employee ID, Name, Grade) from this form. These changes will not be saved automatically ΓÇö you will need to reselect or re-enter a new associate and save the record to persist assignment.
            </Typography>
            <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
              Do you want to continue and change the Supply Source?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelSupplyChange} color="inherit">Cancel</Button>
            <Button onClick={confirmSupplyChange} color="secondary" variant="contained">Proceed</Button>
          </DialogActions>
        </Dialog>

      {/* Confirm To Be Cancelled with employee */}
      <Dialog
        open={confirmCancelledOpen}
        onClose={cancelCancelledChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            Changing Demand Status to <strong>"To Be Cancelled"</strong> will set the Action to <strong>"Release From SO"</strong> and clear the associated employee details (Employee ID, Name, Grade). The employee will become available for other SO recommendations.
          </Typography>
          <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
            Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelCancelledChange} color="inherit">Cancel</Button>
          <Button onClick={confirmCancelledChange} color="secondary" variant="contained">Proceed</Button>
        </DialogActions>
      </Dialog>
      </>

      

       

           <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={5}>
                <Box display="flex" alignItems="center" gap={1}>
                 <Typography
                        variant="h5"
                        sx={{
                          fontWeight: "bold",
                          color: primaryColor,
                          marginBottom: 2,
                          textAlign: "left",
                        }}
                      >
                         Demand View
                      </Typography>
                      <a href="/home" style={{ fontSize: '0.85rem', color: primaryColor, textDecoration: 'none', fontWeight: 'bold', marginBottom: 16, marginLeft: 4, cursor: 'pointer' }}>(V2)</a>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", marginBottom: 2 }}>
                        {`(${filteredRows?.length ?? 0} Demands)`}
                      </Typography>
                      </Box>
                      </Grid>


          <Grid item xs={12} md={7} alignItems="revert">
        <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
        <FormControlLabel
          ref={toggleRef}
          control={
            <Switch
              checked={chartView}
              onChange={handleChartViewToggle}
              color="primary"
            />
          }
          label="Chart View"
        />
        {/* Tour Tooltip */}
        <Popper open={showTour} anchorEl={toggleRef.current} placement="bottom" transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper sx={{ p: 2, maxWidth: 280, bgcolor: "#005EB8", color: "white", position: "relative" }}>
                <IconButton
                  size="small"
                  onClick={closeTour}
                  sx={{ position: "absolute", top: 4, right: 4, color: "white" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  ≡ƒôè Take a Tour!
                </Typography>
                <Typography variant="body2">
                  Enable "Chart View" to explore interactive dashboards with KPIs and visualizations!
                </Typography>
              </Paper>
            </Fade>
          )}
        </Popper>
        <ExportIcon
          exportRows={filteredRows}
          exportColumns={columnsWithActions}
          exportTableRef={gridRef}
          reportName="DemandSupplyMapping_Report"
        />
        <Button 
          size="small" 
          variant="outlined"
          onClick={() => {
            clearAllRef.current && clearAllRef.current();
            // Also clear DataGrid column filters if available
            if (gridRef.current?.setFilterModel) {
              gridRef.current.setFilterModel({ items: [] });
            }
          }}
          sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
        >
          Clear All
        </Button>
        <Tooltip title="Saves filter selections to this browser only. If you switch browsers, you will need to re-save.">
          <Button 
            size="small" 
            variant="contained"
            color="primary"
            onClick={() => {
              if (saveFilterRef.current) {
                saveFilterRef.current();
                setSaveFilterMsg("Filters saved! Note: Saved filters are stored in this browser only. If you switch browsers, you will need to re-save your filters.");
              }
            }}
            sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
          >
            Save Filters
          </Button>
        </Tooltip>
        <Button 
          size="small" 
          variant="outlined"
          onClick={() => setShowFiltersOpen(true)}
          sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
        >
          Show Filters
        </Button>
        </Box>
                </Grid>
                </Grid>

      {/* Save Filter confirmation */}
      <Snackbar open={!!saveFilterMsg} autoHideDuration={5000} onClose={() => setSaveFilterMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setSaveFilterMsg("")}>
          {saveFilterMsg}
        </Alert>
      </Snackbar>

      {/* Show Filters modal */}
      <Dialog open={showFiltersOpen} onClose={() => setShowFiltersOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Active Filters
          <IconButton onClick={() => setShowFiltersOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {(() => {
            const af = activeFiltersRef.current;
            if (!af) return <Typography variant="body2" color="text.secondary">No filters loaded.</Typography>;
            const { filters: f, filtersMeta: meta } = af;
            const hasAny = meta.some(({ key }) => (f[key] || []).length > 0);
            if (!hasAny) return <Typography variant="body2" color="text.secondary">No filters selected.</Typography>;
            return meta.map(({ label, key }) => {
              const vals = f[key] || [];
              if (vals.length === 0) return null;
              return (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{label} ({vals.length})</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {vals.map(v => <Chip key={v} label={v} size="small" variant="outlined" />)}
                  </Box>
                </Box>
              );
            });
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFiltersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>


                
                      {underMaintenance && (
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            {MAINTENANCE_BANNER_MSG}
                          </Alert>
                        </Grid>
                      )}

      {chartView ? (
        <DemandDashboard />
      ) : (
        <>
  <FilterPanel initialData={initialData} 
  setData={setData} 
  template={template}
  onClearAllRef={clearAllRef}
  onSaveFilterRef={saveFilterRef}
  onActiveFiltersRef={activeFiltersRef}
  compact={true} />


      {/* <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <Autocomplete
            options={parentCustomerOptions}
            value={parentCustomer}
            onChange={(event, newValue) => setParentCustomer(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parent Customer"
                variant="outlined"
                size="small"
              />
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter((option) =>
                option.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            isOptionEqualToValue={(option, value) => option === value}
          />
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 300 }}>
          <Autocomplete
            options={buDescOptions}
            value={buDesc}
            onChange={(event, newValue) => setBuDesc(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Business Unit"
                variant="outlined"
                size="small"
              />
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter((option) =>
                option.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            isOptionEqualToValue={(option, value) => option === value}
          />
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="pdl-name-label">PDL Name</InputLabel>
          <Select
            labelId="pdl-name-label"
            id="pdl-name"
            value={pdlName}
            label="PDL Name"
            onChange={handlePdlNameChange}
          >
            <MenuItem value="All">All</MenuItem>
            {pdlNameOptions
              .filter((option) => option !== "All")
              .map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="off-on-label">Off/On</InputLabel>
          <Select
            labelId="off-on-label"
            id="off-on"
            value={offOn}
            label="Off/On"
            onChange={handleOffOnChange}
          >
            <MenuItem value="All">All</MenuItem>
            {offOnOptions
              .filter((option) => option !== "All")
              .map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleViewReport}
        sx={{ mb: 2 }}
      >
        View Report
      </Button> */}

      {/* <InputBase
        type="text"
        placeholder="Global Search..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{
          padding: "6px 12px",
          marginBottom: 2,
          borderRadius: 1,
          backgroundColor: "#F5F5F5",
          border: `1px solid #D3D3D3`,
          "&:focus-within": {
            borderColor: "#005EB8",
          },
          width: "100%",
        }}
      /> */}
      <div >
      

         <Paper sx={{ height:480, p: 1}}>
        <StyledDataGrid
          ref={gridRef}
          rows={filteredRows}
          initialState={{
            pinnedColumns: {
              // Pin the Actions column and the SO Id column (frozen at left)
              left: ["actions", "SO Id"],
              right: [],
            },
          }}
          getRowClassName={(params) => {
            // preserve edit styling
            if (rowModesModel[params.id]?.mode === "edit") return "row-editing";
            const row = params.row || {};
            // Fulfilment Plan driven styling takes precedence
            const fpRaw = row["Fulfilment Plan"] || row["FulfilmentPlan"] || row["fulfilment plan"] || "";
            const fp = String(fpRaw).toLowerCase();
            if (fp.includes("proposed")) return "fp-proposed";
            if (fp.includes("selected") || fp.includes("allocated") || fp.includes("awip")) return "assigned-row";
            // fallback: row is green if an employee is assigned
            const empId = row["Employee Id"] || row["EmployeeId"] || row["employeeId"] || row["Emp Id"] || row["EmpId"];
            const empName = row["Employee Name"] || row["employeeName"] || row["EmployeeName"];
            if ((empId && String(empId).trim() !== "") || (empName && String(empName).trim() !== "")) return "assigned-row";
            return "";
          }}
          columns={columnsWithActions}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          components={{
            Toolbar: () => (
              <GridToolbarContainer
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: 1,
                  borderBottom: `1px solid #D3D3D3`,
                }}
              ></GridToolbarContainer>
            ),
          }}
      pagination 
      pageSize={5} 
      rowsPerPageOptions={[10, 25, 50]}                                             
          getRowId={(row) => row.item_id}
          sx={{
            "& .editable-cell": {
              backgroundColor: "#FFFDE7 !important",
            },
          }}
        />
        </Paper>
      </div>
        </>
      )}

      {/* Audit History Dialog */}
      <Dialog
        open={auditOpen}
        onClose={handleAuditClose}
        fullWidth
       
        maxWidth="lx" // increased popup width
      >
        <DialogTitle>Audit History</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">SO ID: {selectedUnique}</Typography>
          {auditLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <div style={{ height: 400, width: "100%", marginTop: 16 }}>
              <StyledDataGrid
                rows={auditData}
                columns={auditColumns}
                pageSize={5}
                rowsPerPageOptions={[5, 10]}
                disableSelectionOnClick
                getRowId={(row) => row.auditid}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuditClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={validationOpen} onClose={() => setValidationOpen(false)}>
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <Typography>{validationMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DemandSupplyMatchingV1;